import type {
  Audience,
  NewIcon,
  ProofingContext,
  DrawingInput,
  MapDrawing,
  MapLayer,
  MapGrid,
  MapScale,
  NewMapDefaults,
  CategoryDetails,
  MapPoint,
  PinDetails,
  RegionDetails,
  AtlasMap,
  MapDeletionImpact,
  MapIcon,
  MapMarker,
  MapRegion,
  MapSummary,
  MarkerCategory,
  BoardDetails,
  BoardObject,
  BoardObjectDetails,
  BoardSummary,
  IdeaBoard,
  NewBoardObject,
  CalendarDetails,
  CalendarSummary,
  CalendarSystem,
  Era,
  EraInput,
  EventInput,
  MarkerSummary,
  Timeline,
  TimelineDeletionImpact,
  TimelineEvent,
  TimelineEventEntry,
  TimelineNode,
  CollectionSummary,
  DeletionImpact,
  EntryChanges,
  EntryDetail,
  EntryLock,
  EntrySummary,
  ExportData,
  ParsedTable,
  RandomTable,
  CharacterSheet,
  SheetData,
  SheetTemplate,
  TemplateChoice,
  TemplateData,
  TemplateEdit,
  TemplateRef,
  GeneratedEntry,
  GeneratorKey,
  SavedScript,
  ScriptPack,
  ScriptSummary,
  TableEntry,
  TableSummary,
  GraphData,
  NewEntry,
  NewImage,
  ReplaceChoice,
  ReplaceOptions,
  ReplaceResult,
  SearchQuery,
  SearchResults,
  RealmCounts,
  RealmInfo,
  StoryDeletionImpact,
  StoryLevel,
  StoryOutline,
  StoryProjectInput,
  StoryProjectSummary,
  StoryScene,
  SceneChanges,
  SceneRevision,
  WritingSession,
  WritingSessionInput,
  IgnoredMention,
  ConsistencyReport,
  Finding,
  Campaign,
  CampaignChanges,
  CampaignSession,
  CampaignSummary,
  EncounterDetails,
  EncounterSummary,
  Quest,
  QuestChanges,
  SessionChanges,
  Encounter,
  RunCommand,
  RunResult,
  Condition,
  ModeWorldData,
} from '@realmery/realm-core';

/**
 * The typed request/response contract between the page and the realm database worker
 * (realm.worker.ts). Only plain data crosses: no functions or live objects.
 */
export interface RealmSummary extends RealmInfo {
  /** The realm's storage file inside the browser's private file system. */
  readonly file: string;
  readonly entryCount: number;
}

export type DuplicateChoice = 'replace' | 'copy';

/**
 * Where a realm's backup stands (docs/SYNC-PLAN.md section 4.5, db/sync.ts):
 *
 * - 'synced': the server has this copy; 'pending': changes wait to upload;
 *   'uploading'; 'offline': waiting for the connection;
 * - 'remote': only on the server, downloaded when opened; 'downloading';
 * - 'conflict' and 'deleted-elsewhere': another device changed or deleted it first, so
 *   this copy isn't uploaded over it;
 * - 'full' (the account's quota is used up) and 'too-large' (larger than the whole quota);
 * - 'unavailable' (the server's sync is off), 'signed-out', and 'error' (retried);
 * - 'off': backing up is turned off for this realm on this device.
 */
export type SyncStatus =
  | 'synced'
  | 'pending'
  | 'uploading'
  | 'offline'
  | 'remote'
  | 'downloading'
  | 'conflict'
  | 'deleted-elsewhere'
  | 'full'
  | 'too-large'
  | 'unavailable'
  | 'signed-out'
  | 'error'
  | 'off';

export interface SyncInfo {
  readonly status: SyncStatus;
  /** When the server last accepted this realm from this device: ISO, or null for never. */
  readonly backedUpAt: string | null;
  /**
   * Set on a realm kept as a copy (docs/SYNC-PLAN.md 4.4): this device's changes, when
   * another device changed the realm first ('conflict') or deleted it ('deleted').
   */
  readonly copy?: 'conflict' | 'deleted';
}

/** A version the server keeps of a realm (Versions…). */
export interface RealmVersion {
  readonly version: number;
  readonly size: number;
  readonly createdAt: string;
  readonly device: string;
  readonly current: boolean;
}

/** A realm deleted within the last 30 days, restorable (Recently deleted). */
export interface DeletedRealm {
  readonly file: string;
  readonly name: string;
  readonly entryCount: number;
  readonly deletedAt: string;
}

/** The account's backup space: bytes used by its realms' current versions, of its quota. */
export interface BackupUsage {
  readonly used: number;
  readonly quota: number;
}

export type ImportOutcome =
  | { readonly status: 'imported'; readonly realm: RealmSummary; readonly upgradedFrom: number }
  | { readonly status: 'duplicate'; readonly existing: RealmSummary; readonly incoming: RealmInfo };

/**
 * A realm someone shares with this account, kept on this device to read
 * (docs/SHARING-PLAN.md section 3.4): their edition for this account's role. Its file is
 * a realm file of its own (`shareFile`), never backed up or listed with this account's
 * realms, and it opens read-only.
 */
export interface SharedCopy {
  readonly file: string;
  readonly ownerId: string;
  readonly ownerEmail: string;
  /** The owner's realm. */
  readonly realmId: string;
  /**
   * 'player' or 'reader': the edition's audience too. 'editor' is a Co-owner's: the
   * owner's realm itself (docs/SHARING-CO-OWNERS-PLAN.md section 3), written back to them.
   */
  readonly role: string;
  readonly name: string;
  /** The edition's version on the server. */
  readonly version: number;
  /**
   * Set on a preview (docs/SHARING-CO-OWNERS-PLAN.md section 2): the owner's own realm,
   * whose copy for `role` this is, built here and held in memory only.
   */
  readonly previewOf?: string;
}

/** A shared copy as downloaded: its sealed edition, and the edition key from the owner's letter. */
export interface NewSharedCopy extends SharedCopy {
  readonly key: Uint8Array;
  readonly bytes: Uint8Array;
}

/** A fogged map's picture and thumbnail for an edition (realm-core edition.ts), by map id. */
export type FoggedPictures = Readonly<Record<string, { readonly image: Uint8Array; readonly thumbnail: Uint8Array | null }>>;

export interface OpenedRealm {
  readonly realm: RealmSummary;
  /** Set when the realm is someone else's, shared with this account to read, or co-owned with them. */
  readonly shared: SharedCopy | null;
  /**
   * Nothing can be changed here: a Player's or Reader's copy, a preview, or a realm whose
   * edit lease someone else holds (docs/SHARING-CO-OWNERS-PLAN.md section 3.4).
   */
  readonly readOnly: boolean;
  readonly counts: RealmCounts;
  /** Who the whole realm is for (the desktop's File > Realm Audience…). */
  readonly audience: Audience;
  readonly collections: readonly CollectionSummary[];
  readonly entries: readonly EntrySummary[];
}

/** The Chronology page (docs/PARITY-STEP-3-PLAN.md section 4): the tree, and the open timeline if any. */
export interface ChronologyView {
  readonly tree: readonly TimelineNode[];
  /** Null when none is open, or the one asked for is gone. */
  readonly timeline: Timeline | null;
  readonly eras: readonly Era[];
  /** The open timeline's own events, by date. */
  readonly events: readonly TimelineEvent[];
  /** Its events and its sub-timelines', by date, for Include sub-timelines. */
  readonly merged: readonly TimelineEventEntry[];
  /** The realm's active calendar, to show dates in. */
  readonly calendar: CalendarSystem | null;
  /** Why the active calendar can't be used, when it can't. */
  readonly calendarProblem: string | null;
  readonly markers: readonly MarkerSummary[];
}

/** A story project's page (docs/PARITY-STEP-5-PLAN.md section 3): its outline, and the open scene if any. */
export interface StoryProjectView {
  readonly outline: StoryOutline;
  /** The scene asked for, when it's one of this project's. */
  readonly scene: SceneView | null;
  /** The project's writing log, newest first. */
  readonly sittings: readonly WritingSession[];
}

/** An open scene (docs/PARITY-STEP-5-PLAN.md section 4), with what its page links to. */
export interface SceneView {
  readonly scene: StoryScene;
  /** Its revisions, newest first, without their text. */
  readonly revisions: readonly SceneRevision[];
  /** Every Chronology event, to link to. */
  readonly events: readonly TimelineEventEntry[];
  /** The realm's active calendar, for Pick a date; null when there's none or it can't be used. */
  readonly calendar: CalendarSystem | null;
}

/** A map, open (docs/PARITY-STEP-4-PLAN.md section 4), with everything drawn on it. */
export interface AtlasMapView {
  readonly map: AtlasMap;
  /** By when they were made. */
  readonly markers: readonly MapMarker[];
  readonly regions: readonly MapRegion[];
  /** By name. */
  readonly categories: readonly MarkerCategory[];
  /** The realm's imported icons, for pins that show one. */
  readonly icons: readonly MapIcon[];
  /** From the top-level map down to this one. */
  readonly trail: readonly { readonly id: string; readonly name: string }[];
  /** Every map's name, for the maps pins and regions open. */
  readonly maps: readonly { readonly id: string; readonly name: string }[];
  /** Drawing layers, bottom first, and their drawings in the same order. */
  readonly layers: readonly MapLayer[];
  readonly drawings: readonly MapDrawing[];
}

/** A board, open (docs/PARITY-STEP-4-PLAN.md section 3), with what its cards can link to. */
export interface BoardView {
  readonly board: IdeaBoard;
  /** By stacking order, then by when they were made. */
  readonly objects: readonly BoardObject[];
  /** The realm's maps, by name. */
  readonly maps: readonly { readonly id: string; readonly name: string }[];
  /** Every Chronology event, with its timeline. */
  readonly events: readonly TimelineEventEntry[];
}

/** A campaign's Prep screen (docs/PARITY-STEP-5-PLAN.md section 7). */
export interface CampaignView {
  readonly campaign: Campaign;
  /** By number. */
  readonly sessions: readonly CampaignSession[];
  /** By when they were made. */
  readonly encounters: readonly EncounterSummary[];
  /** By when they were made. */
  readonly quests: readonly Quest[];
  /** The Run screen's, when it was asked for. */
  readonly run: RunData | null;
}

/** An entry on the Run screen, as its party and cast list it. */
export interface RunEntry {
  readonly id: string;
  readonly title: string;
  readonly typeName: string;
}

/** A resource on a sheet, with − and + on the party card. */
export interface RunResource {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly max: number;
  readonly hitPoints: boolean;
}

/** A player character's sheet, as the party card shows it. */
export interface PartySheet {
  readonly id: string;
  readonly name: string;
  readonly templateName: string;
  /** "Armor Class 16", when the template has a defense. */
  readonly defense: string | null;
  readonly resources: readonly RunResource[];
  readonly conditions: readonly Condition[];
  /** The conditions its template offers. */
  readonly presets: readonly string[];
}

/** A combatant's sheet: what the runner says about it. */
export interface CombatantSheet {
  readonly sheetId: string;
  readonly name: string;
  readonly summary: string;
  /** Hit points and conditions kept in step with the sheet. */
  readonly shares: boolean;
}

/** The Run screen (docs/PARITY-STEP-5-PLAN.md section 8): the party, the encounter being run, and the dock's tables. */
export interface RunData {
  /** By title. */
  readonly party: readonly { readonly entry: RunEntry; readonly sheet: PartySheet | null }[];
  /** By title, each with its sheet's one-line summary. */
  readonly cast: readonly { readonly entry: RunEntry; readonly sheetId: string | null; readonly summary: string }[];
  readonly active: Encounter | null;
  readonly combatantSheets: Readonly<Record<string, CombatantSheet>>;
  readonly conditionPresets: Readonly<Record<string, readonly string[]>>;
  readonly tables: readonly { readonly id: string; readonly name: string }[];
}

/** The Calendar page's gallery (docs/PARITY-STEP-3-PLAN.md section 5). */
export interface CalendarsView {
  readonly calendars: readonly CalendarSummary[];
  readonly activeId: string | null;
}

/** One calendar, or why it can't be shown: a month with no days, or a moon with no cycle. */
export interface CalendarView {
  readonly id: string;
  readonly calendar: CalendarSystem | null;
  readonly problem: string | null;
  readonly active: boolean;
}

/** A realm's sheets and templates, for the Character sheets page. */
export interface SheetsOverview {
  readonly sheets: readonly CharacterSheet[];
  readonly templates: readonly SheetTemplate[];
  /** Each linked entry's title, by id. */
  readonly entryTitles: Readonly<Record<string, string>>;
}

/** A realm's saved scripts, and the Quick Generators the desktop has its own (Python) versions of. */
export interface ScriptsOverview {
  readonly scripts: readonly ScriptSummary[];
  readonly overrides: Readonly<Partial<Record<GeneratorKey, { readonly source: string; readonly modified: string }>>>;
}

/** One sheet, with its template and (if linked) its entry's title. */
export interface SheetView {
  readonly sheet: CharacterSheet;
  readonly template: SheetTemplate;
  readonly entryTitle: string | null;
}

/** An entry a sheet could belong to: one without a sheet. */
export interface SheetEntryChoice {
  readonly id: string;
  readonly title: string;
  readonly entityType: string;
  readonly customTypeName: string | null;
}

export type TableImportTarget = { readonly kind: 'new' } | { readonly kind: 'append' | 'replace'; readonly tableId: string };

export interface RealmApi {
  /**
   * Hands the worker the account's realms key (docs/REALM-ENCRYPTION.md section 2), a
   * non-extractable key derived from the master key. Every other call waits for it.
   */
  unlock(realmsKey: CryptoKey): void;
  listRealms(): RealmSummary[];
  createRealm(name: string, description: string): RealmSummary;
  importRealm(bytes: Uint8Array, onDuplicate?: DuplicateChoice): ImportOutcome;
  exportRealm(file: string): { fileName: string; bytes: Uint8Array };
  deleteRealm(file: string): void;
  openRealm(file: string): OpenedRealm;
  /**
   * Sharing (docs/SHARING-PLAN.md sections 3.1 and 3.4). `buildEdition` makes this
   * account's realm's edition for an audience and seals it under `key` (32 bytes) for
   * uploading; the realm itself is unchanged. The others keep realms shared with this
   * account on this device.
   */
  buildEdition(file: string, audience: 'player' | 'reader', fogged: FoggedPictures, key: Uint8Array): { bytes: Uint8Array };
  /** Opens that edition read-only, unsealed and kept nowhere, as a realm file of its own until another realm opens. */
  openPreview(file: string, audience: 'player' | 'reader', fogged: FoggedPictures): { file: string };
  storeShared(copy: NewSharedCopy): void;
  sharedCopies(): SharedCopy[];
  forgetShared(file: string): void;
  /**
   * Co-owners (docs/SHARING-CO-OWNERS-PLAN.md section 3). `coOwnedState` says which
   * version of the owner's realm a co-owned copy came from, whether it has changes not sent
   * yet, and which key it's kept with; null for any other realm. `setLease` makes the
   * realm read-only here while someone else holds its edit lease, and writable again.
   * `catchUp` takes the server's newer version, if this copy has nothing unsent. The owner
   * puts the realm key in a co-owner's letter (`realmKeyRaw`), and seals the realm under a
   * new one when a co-owner goes (`rekeyRealm`, once it's uploaded).
   */
  coOwnedState(file: string): { base: number; dirty: boolean; keyId: string | null } | null;
  setLease(file: string, held: boolean): void;
  catchUp(file: string): boolean;
  /** Sends what's waiting to upload now, before the lease is given up; says whether the server has it all. */
  sendNow(file: string): boolean;
  realmKeyRaw(file: string): { key: Uint8Array };
  rekeyRealm(file: string): { key: Uint8Array };
  getEntry(file: string, entryId: string): EntryDetail | null;
  /**
   * Entry writes (docs/ENTRY-EDITOR-PLAN.md section 4.4). Each applies the change, then
   * resolves once the realm has been sealed with it. Saves run one at a time.
   */
  createEntry(file: string, entry: NewEntry): { id: string };
  updateEntry(file: string, entryId: string, changes: EntryChanges): void;
  deleteEntry(file: string, entryId: string): void;
  deletionImpact(file: string, entryId: string): DeletionImpact;
  /**
   * Locks (docs/ENTRY-EDITOR-SLICE-2-PLAN.md section 4.6). The page encrypts and decrypts,
   * so only the encrypted text and the plaintext it checks against come here, never a
   * passphrase. Both resolve once the realm is sealed with the change.
   */
  lockEntry(file: string, entryId: string, lock: EntryLock): void;
  removeLock(file: string, entryId: string, lock: EntryLock): void;
  /** What the editor suggests: the realm's relationship types and metadata field names. */
  editorSuggestions(file: string): EditorSuggestions;
  /**
   * Pictures (docs/ENTRY-EDITOR-SLICE-3-PLAN.md section 3.2): PNG, JPEG or GIF bytes, the
   * page having converted anything else. `id` and `created` put back an image whose
   * removal was undone.
   */
  addImage(file: string, entryId: string, image: NewImage, id?: string, created?: string): { id: string; created: string };
  removeImage(file: string, imageId: string): void;
  /** Collections (section 3.3). Each change is written at once. */
  createCollection(file: string, name: string): { id: string };
  setEntryCollections(file: string, entryId: string, collectionIds: readonly string[]): void;
  setCollectionAudience(file: string, collectionId: string, audience: Audience): void;
  renameCollection(file: string, collectionId: string, name: string): void;
  /** Its entries stay; only their membership goes. */
  deleteCollection(file: string, collectionId: string): void;
  /** Random tables (docs/PARITY-STEP-2-PLAN.md section 4). Rows are a table's whole list, written together. */
  listTables(file: string): TableSummary[];
  getTable(file: string, tableId: string): RandomTable | null;
  createTable(file: string, name: string, description: string): { id: string };
  updateTable(file: string, tableId: string, name: string, description: string, entries: readonly TableEntry[]): void;
  /** Imported tables: each a new table, or the first one's rows added to or replacing an existing table's. */
  importTables(file: string, tables: readonly ParsedTable[], target: TableImportTarget): { ids: string[] };
  deleteTable(file: string, tableId: string): void;

  /** Chronology (docs/PARITY-STEP-3-PLAN.md section 4). Each write resolves once the realm is sealed with it. */
  chronology(file: string, timelineId: string | null): ChronologyView;
  createTimeline(file: string, name: string, description: string, parentId: string | null): { id: string };
  updateTimeline(file: string, timelineId: string, name: string, description: string): void;
  /** What deleting it takes too: sub-timelines, eras, events and scene links. */
  timelineDeletionImpact(file: string, timelineId: string): TimelineDeletionImpact;
  deleteTimeline(file: string, timelineId: string): void;
  createEra(file: string, timelineId: string, era: EraInput): { id: string };
  updateEra(file: string, eraId: string, era: EraInput): void;
  /** Its events stay, unfiled. */
  deleteEra(file: string, eraId: string): void;
  createEvent(file: string, timelineId: string, event: EventInput): { id: string };
  updateEvent(file: string, eventId: string, event: EventInput): void;
  /** How many Story Studio scenes link to it. */
  eventSceneLinks(file: string, eventId: string): number;
  deleteEvent(file: string, eventId: string): void;

  /** Story Studio's outline (docs/PARITY-STEP-5-PLAN.md section 3). */
  storyProjects(file: string): StoryProjectSummary[];
  /** Null when there's no such project. `sceneId` opens one of its scenes. */
  storyProject(file: string, projectId: string, sceneId: string | null): StoryProjectView | null;
  createStoryProject(file: string, project: StoryProjectInput): { id: string };
  updateStoryProject(file: string, projectId: string, project: StoryProjectInput): void;
  /** What deleting it takes too. */
  storyDeletionImpact(file: string, level: StoryLevel, id: string): StoryDeletionImpact;
  /** Deletes a project, act, chapter or scene, with everything inside it. */
  deleteStoryItem(file: string, level: StoryLevel, id: string): void;
  createAct(file: string, projectId: string, title: string, notes: string): { id: string };
  updateAct(file: string, actId: string, title: string, notes: string): void;
  createChapter(file: string, actId: string, title: string, notes: string): { id: string };
  updateChapter(file: string, chapterId: string, title: string, notes: string): void;
  createScene(file: string, chapterId: string, title: string, wordGoal: number): { id: string };
  /** A scene's title and word goal. */
  updateSceneOutline(file: string, sceneId: string, title: string, wordGoal: number): void;
  /** One place up (-1) or down (1) among its siblings. */
  moveStoryItem(file: string, level: 'act' | 'chapter' | 'scene', id: string, direction: -1 | 1): void;
  /** Writes what changed; changed text is kept as a revision, with the newest `keepRevisions` kept. */
  updateScene(file: string, sceneId: string, changes: SceneChanges, keepRevisions: number): void;
  /** One revision's text, or null once it's gone. */
  sceneRevision(file: string, revisionId: string): string | null;
  restoreRevision(file: string, sceneId: string, revisionId: string, keepRevisions: number): void;
  /** A focus-mode sitting for the writing log; false when too brief to keep. */
  recordWritingSession(file: string, session: WritingSessionInput): boolean;
  /** What Scan for Connections was told to leave alone in `scope` (a scene's id), by label. */
  ignoredMentions(file: string, scope: string): IgnoredMention[];
  ignoreMention(file: string, scope: string, key: string, label: string): void;
  stopIgnoringMention(file: string, scope: string, key: string): void;

  /** The Game Master Toolkit's Prep (docs/PARITY-STEP-5-PLAN.md section 7). */
  campaigns(file: string): CampaignSummary[];
  /** Null when there's no such campaign. `withRun` adds the Run screen's party, encounter and tables. */
  campaign(file: string, campaignId: string, withRun: boolean): CampaignView | null;
  /** The encounter the Run screen runs; null for none. */
  setActiveEncounter(file: string, campaignId: string, encounterId: string | null): void;
  /** One thing done at the table; `stamp` is the time here, for the play log. */
  runEncounter(file: string, campaignId: string, encounterId: string, command: RunCommand, stamp: string): RunResult;
  /** One more or one less of a resource on a party member's sheet. */
  stepSheetResource(file: string, campaignId: string, sheetId: string, fieldKey: string, delta: -1 | 1, stamp: string): void;
  setSheetConditions(file: string, campaignId: string, sheetId: string, conditions: readonly Condition[], line: string, stamp: string): void;
  /** A line in the current session's play log: a roll, a table's result. */
  appendPlayLog(file: string, campaignId: string, line: string, stamp: string): void;
  /** The Atlas (docs/PARITY-STEP-4-PLAN.md section 4). */
  maps(file: string): MapSummary[];
  atlasMap(file: string, mapId: string): AtlasMapView | null;
  importMap(file: string, name: string, image: Uint8Array, thumbnail: Uint8Array, defaults: NewMapDefaults): { id: string };
  updateMapSettings(file: string, mapId: string, scale: MapScale, grid: MapGrid): void;
  /** A realm's name and description, from its overview's Edit details. */
  updateRealmDetails(file: string, name: string, description: string): void;
  /** Spelling (docs/PARITY-STEP-6-PLAN.md section 6): the realm's names, its dictionary, and what's ignored in a scene or entry. */
  proofingContext(file: string, scopeId: string | null): ProofingContext;
  addToRealmDictionary(file: string, word: string): void;
  removeFromRealmDictionary(file: string, word: string): void;
  ignoreSpelling(file: string, scopeId: string, key: string): void;
  /** The Icon Library (docs/PARITY-STEP-4-PLAN.md section 10): the realm's imported icons, by pack and name. */
  icons(file: string): MapIcon[];
  importIcons(file: string, icons: readonly NewIcon[]): { ids: string[] };
  renameIcon(file: string, iconId: string, name: string): void;
  /** How many pins and categories show it. */
  iconUsage(file: string, iconId: string): number;
  deleteIcon(file: string, iconId: string): void;
  /** Fog of war (docs/PARITY-STEP-4-PLAN.md section 8): on or off, and the revealed area as `MapFog.encode` writes it. */
  updateFog(file: string, mapId: string, enabled: boolean, revealed: string): void;
  /** Drawing (docs/PARITY-STEP-4-PLAN.md section 7). */
  createLayer(file: string, mapId: string, name: string): { id: string };
  updateLayer(file: string, layerId: string, name: string, playersCanSee: boolean): void;
  moveLayer(file: string, layerId: string, up: boolean): void;
  /** How many drawings went with it. */
  deleteLayer(file: string, layerId: string): number;
  saveDrawing(file: string, mapId: string, drawing: DrawingInput): void;
  deleteDrawing(file: string, mapId: string, drawingId: string): void;
  /** Undo and Redo: the map's drawings put back to exactly these. */
  restoreDrawings(file: string, mapId: string, drawings: readonly DrawingInput[]): void;
  renameMap(file: string, mapId: string, name: string): void;
  /** For a map imported before thumbnails, as the desktop makes them once. */
  storeMapThumbnail(file: string, mapId: string, thumbnail: Uint8Array): void;
  mapDeletionImpact(file: string, mapId: string): MapDeletionImpact;
  deleteMap(file: string, mapId: string): MapDeletionImpact;
  /** Editing a map (docs/PARITY-STEP-4-PLAN.md section 5). */
  createPin(file: string, mapId: string, at: MapPoint, categoryId: string | null): { id: string };
  movePin(file: string, pinId: string, at: MapPoint): void;
  updatePin(file: string, pinId: string, details: PinDetails): void;
  /** How many Chronology events named it. */
  deletePin(file: string, pinId: string): number;
  createRegion(file: string, mapId: string, points: readonly MapPoint[], categoryId: string | null): { id: string };
  updateRegion(file: string, regionId: string, details: RegionDetails): void;
  deleteRegion(file: string, regionId: string): void;
  createCategory(file: string, details: CategoryDetails): { id: string };
  updateCategory(file: string, categoryId: string, details: CategoryDetails): void;
  /** How many pins and regions use it, on every map. */
  categoryUse(file: string, categoryId: string): number;
  deleteCategory(file: string, categoryId: string): number;

  /** The Idea Board (docs/PARITY-STEP-4-PLAN.md section 3). */
  boards(file: string): BoardSummary[];
  board(file: string, boardId: string): BoardView | null;
  createBoard(file: string, details: BoardDetails): { id: string };
  updateBoard(file: string, boardId: string, details: BoardDetails): void;
  /** How many objects went with it. */
  deleteBoard(file: string, boardId: string): number;
  createBoardObject(file: string, boardId: string, object: NewBoardObject): { id: string };
  createArrow(file: string, boardId: string, fromId: string, toId: string): { id: string };
  moveBoardObject(file: string, objectId: string, x: number, y: number): void;
  updateBoardObject(file: string, objectId: string, details: BoardObjectDetails): void;
  bringToFront(file: string, objectId: string): void;
  /** How many arrows went with it. */
  deleteBoardObject(file: string, objectId: string): number;

  createCampaign(file: string, name: string, description: string): { id: string };
  updateCampaign(file: string, campaignId: string, changes: CampaignChanges): void;
  /** With its sessions, encounters and quests. */
  deleteCampaign(file: string, campaignId: string): void;
  /** The next session, numbered after the ones there are. */
  createSession(file: string, campaignId: string): { id: string };
  updateSession(file: string, sessionId: string, changes: SessionChanges): void;
  /** Its encounters stay, with no session. */
  deleteSession(file: string, sessionId: string): void;
  /** The session the play log writes to; null for none. */
  setCurrentSession(file: string, campaignId: string, sessionId: string | null): void;
  createEncounter(file: string, campaignId: string, details: EncounterDetails): { id: string };
  updateEncounterDetails(file: string, encounterId: string, details: EncounterDetails): void;
  /** With its combatants. */
  deleteEncounter(file: string, encounterId: string): void;
  createQuest(file: string, campaignId: string): { id: string };
  updateQuest(file: string, questId: string, changes: QuestChanges): void;
  deleteQuest(file: string, questId: string): void;

  /** Knowledge Modes' snapshot of the realm (docs/PARITY-STEP-5-PLAN.md section 9), with the Consistency Check's active findings. */
  knowledgeModes(file: string): ModeWorldData;

  /** The Consistency Check (docs/PARITY-STEP-5-PLAN.md section 6), run here so the page stays responsive. */
  consistency(file: string): ConsistencyReport;
  /** Kept by the finding's fingerprint, with the note. */
  dismissFinding(file: string, finding: Finding, note: string): void;
  restoreFinding(file: string, fingerprint: string): void;

  /** Calendars (docs/PARITY-STEP-3-PLAN.md section 5). */
  calendars(file: string): CalendarsView;
  /** Null when there's no such calendar. */
  calendar(file: string, calendarId: string): CalendarView | null;
  createCalendar(file: string, name: string, description: string): { id: string };
  /** The whole calendar, every part replaced. */
  updateCalendar(file: string, calendarId: string, details: CalendarDetails): void;
  /** Unsets it first if it's the active one. */
  deleteCalendar(file: string, calendarId: string): void;
  setActiveCalendar(file: string, calendarId: string | null): void;
  /** Every event whose dates include the day, for the Date Lookup. */
  eventsOnDay(file: string, day: bigint): TimelineEventEntry[];
  /** Every event touching the days `from` to `to`: a calendar's month at once. */
  eventsBetween(file: string, from: bigint, to: bigint): TimelineEventEntry[];
  /** The day the latest event starts on, or null with none. */
  lastEventDay(file: string): bigint | null;

  /** Character sheets (docs/PARITY-STEP-2-PLAN.md section 5). */
  sheetsOverview(file: string): SheetsOverview;
  getSheet(file: string, sheetId: string): SheetView | null;
  /** The entry's sheet, if it has one. */
  entrySheet(file: string, entryId: string): SheetView | null;
  /** The realm's templates, then the built-ins it has no copy of. */
  templateChoices(file: string): TemplateChoice[];
  /** Entries without a sheet: people and creatures first, then by title. */
  sheetEntryChoices(file: string): SheetEntryChoice[];
  /** A built-in is copied into the realm the first time a sheet uses it. */
  createSheet(file: string, template: TemplateRef, name: string, entryId: string | null): { id: string };
  /** What's written on the sheet: its whole set of values. */
  setSheetValues(file: string, sheetId: string, values: ReadonlyMap<string, string>): void;
  renameSheet(file: string, sheetId: string, name: string): void;
  /** The entry the sheet belongs to, or null for none. */
  linkSheet(file: string, sheetId: string, entryId: string | null): void;
  deleteSheet(file: string, sheetId: string): void;
  importSheet(file: string, data: SheetData): { id: string };
  getTemplate(file: string, templateId: string): { template: SheetTemplate; sheetCount: number } | null;
  createTemplate(file: string, name: string): { id: string };
  duplicateTemplate(file: string, templateId: string): { id: string };
  templateFromBuiltIn(file: string, builtinKey: string): { id: string };
  /** `renames` (old key -> new) moves what sheets have written under a renamed field. */
  updateTemplate(file: string, templateId: string, edit: TemplateEdit, renames: Readonly<Record<string, string>>): void;
  /** Refused while a sheet uses it. */
  deleteTemplate(file: string, templateId: string): void;
  importTemplate(file: string, data: TemplateData): { id: string };

  /** Generators and scripting (docs/PARITY-STEP-2-PLAN.md section 6). Scripts run in the sandbox, never here. */
  scriptsOverview(file: string): ScriptsOverview;
  getScript(file: string, scriptId: string): SavedScript | null;
  /** A new JavaScript script. */
  createScript(file: string, name: string, description: string, source: string): { id: string };
  /** Refused for a Python script, which is the desktop's to change. */
  updateScript(file: string, scriptId: string, name: string, description: string, source: string): void;
  deleteScript(file: string, scriptId: string): void;
  /** A pack's scripts added, each in its own language, and its generators saved as the desktop's versions; nothing runs. */
  importScriptPack(file: string, pack: ScriptPack): { scriptIds: string[] };
  /** The entries a generator or script run made, once you've chosen them. */
  createGeneratedEntries(file: string, entries: readonly GeneratedEntry[]): { ids: string[] };
  /** The realm gathered for the JSON, Markdown and HTML exports, which the page builds. */
  exportData(file: string): ExportData;
  /** The realm's own audience, written at once. */
  setRealmAudience(file: string, audience: Audience): void;
  /** Finds text in the realm's entries (docs/SEARCH-PLAN.md): plain text, with Match case and Whole words. */
  search(file: string, query: SearchQuery): SearchResults;
  /**
   * Replaces in the chosen entries (docs/REPLACE-PLAN.md), after keeping a copy of the
   * realm for `undoReplace`. Entries changed since they were searched are skipped.
   */
  replace(file: string, query: SearchQuery, replacement: string, choices: readonly ReplaceChoice[], options: ReplaceOptions): ReplaceResult;
  /** Puts the realm back as it was before the last replace, if nothing has changed since ('conflict' otherwise). */
  undoReplace(file: string): void;
  /** Every entry and link, for the knowledge graph (docs/KNOWLEDGE-GRAPH-PLAN.md). */
  graph(file: string): GraphData;
  /** Each realm's backup state, by realm file. Changes after this arrive as `sync` events (WorkerEvent). */
  syncStates(): Record<string, SyncInfo>;
  /** Uploads whatever is waiting now: the connection came back, or the page is being hidden. */
  syncNow(): void;
  /** Turns backing up this realm from this device on or off (decision 9.1). */
  setBackup(file: string, on: boolean): void;
  /** Clears the note on a realm kept as a copy. */
  dismissCopyNote(file: string): void;
  /** The versions the server keeps of a realm, newest first. Throws offline. */
  realmVersions(file: string): RealmVersion[];
  /**
   * Makes an older version current, as a new version, and brings it here. Changes here
   * not backed up yet are kept first as a copy, so nothing is lost.
   */
  restoreVersion(file: string, version: number): void;
  /** Realms deleted within 30 days, from the server. Empty offline. */
  deletedRealms(): DeletedRealm[];
  /** Brings a deleted realm back, as it was; it downloads when opened. */
  restoreRealm(file: string): void;
  /** The account's backup space, or null offline. */
  backupUsage(): BackupUsage | null;
}

export interface EditorSuggestions {
  readonly relationshipTypes: readonly string[];
  readonly metadataNames: readonly string[];
  /** The bytes of pictures in the realm, for the note about slower saves. */
  readonly imageBytes: number;
}

export type Async<T> = {
  [K in keyof T]: T[K] extends (...args: infer A) => infer R ? (...args: A) => Promise<Awaited<R>> : never;
};

export type RealmMethod = keyof RealmApi;

export interface WorkerRequest {
  readonly id: number;
  readonly method: RealmMethod;
  readonly args: readonly unknown[];
}

export interface WorkerError {
  readonly name: string;
  readonly message: string;
  /**
   * RealmFileError codes, plus 'too-new', 'storage-unavailable', 'not-found' and 'locked'
   * (the realms key is not unlocked), and for entry writes 'entry-locked', 'invalid' and
   * 'conflict' (the entry changed while it was being locked or unlocked).
   */
  readonly code?: string;
}

export type WorkerResponse =
  | { readonly id: number; readonly ok: true; readonly value: unknown }
  | { readonly id: number; readonly ok: false; readonly error: WorkerError };

/**
 * What the worker reports without being asked: a realm's backup changed state, or the
 * list of realms changed because of another device (a newer version taken, or a realm
 * deleted there).
 */
export type WorkerEvent =
  | { readonly event: 'sync'; readonly file: string; readonly info: SyncInfo }
  | { readonly event: 'realms-changed' };

/**
 * The worker's name carries the account whose realms it serves; the worker keeps them in
 * that account's own storage pool. User ids are the server's `u_` plus base64url, which
 * is safe in a storage name (account/access.ts checks it).
 */
const WORKER_NAME = /^realmery-realms:(u_[A-Za-z0-9_-]{1,64})$/;

export function realmWorkerName(userId: string): string {
  const name = `realmery-realms:${userId}`;
  if (!WORKER_NAME.test(name)) throw new Error('Not a user id');
  return name;
}

/** The account a realm worker serves, from its name, or null if the name is not one of ours. */
export function userOfWorker(name: string): string | null {
  return WORKER_NAME.exec(name)?.[1] ?? null;
}

/** The folder in the browser's private file system that holds one account's sealed realms. */
export function realmFolderName(userId: string): string {
  return `realmery-sealed-${userId}`;
}

/**
 * The `opfs-sahpool` pool that held one account's realms unencrypted, before realms were
 * sealed. Its realms are sealed on the first unlock and the pool is deleted.
 */
export function realmPoolName(userId: string): string {
  return `realmery-${userId}`;
}

/** Storage pools from before realms were kept per account: test data only, cleared on first use. */
export const RETIRED_POOLS = ['realmery-realms'] as const;

/** Realm storage files are named by the app, never by a realm's contents. */
export const REALM_FILE_PATTERN = /^\/realm-([0-9a-f-]{36})\.realmery$/;

export function realmKey(file: string): string {
  const match = REALM_FILE_PATTERN.exec(file);
  if (!match?.[1]) throw new Error(`Not a realm file: ${file}`);
  return match[1];
}

export function realmFile(key: string): string {
  const file = `/realm-${key}.realmery`;
  if (!REALM_FILE_PATTERN.test(file)) throw new Error('Unknown realm');
  return file;
}
